import { HttpBaseApi, HttpError, RequestOptions } from '@forto-dev/ui-platform-http'

import { removeTokenAtom } from '@/features/auth/api/auth.store'
import { buildUrl } from '@/routes/utils'
import { getCurrentToken } from '@/utils/auth'
import { error as logError } from '@/utils/logger'

type SerializationStrategy = (params: object) => string

export type ErrorOptions = {
  disableDefaultErrorDisplay?: boolean
  notifyOnError?: boolean
  /**
   * Suppress the default `logError('HttpError', …)` call for this request.
   * Use for requests where a failure is expected and handled by the caller
   * (e.g. polling a freshly-created shipment that may not be queryable yet),
   * so it doesn't flood console + RUM error tracking with noise.
   */
  errorLoggingHandledUpstream?: boolean
}

export type Options = {
  params?: object
  urlPrefix?: string
  serializationStrategy?: SerializationStrategy
} & ErrorOptions

const getURLSearchString = (params: object) => {
  const u = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => u.append(key, v))
    } else {
      u.append(key, value)
    }
  })
  return u.toString()
}

/**
 * Provides an interface that helps standardizing http requests from front-end
 * @example
 * const client = new Client('http://localhost:3000')
 * const response = await client.get<UserResponse>('/user', { params: { id: 1 } })
 * console.log(response)
 * @see HttpBaseApi
 * @override onError
 * @override onBeforeRequest
 */
class Client {
  private baseUrl: string
  private onErrorCB: ((error: unknown, errorOptions: ErrorOptions) => void) | undefined
  constructor(baseUrl: string, onErrorCB?: (error: unknown, errorOptions: ErrorOptions) => void) {
    this.baseUrl = baseUrl
    this.onErrorCB = onErrorCB || undefined
  }
  /**
   * This method is called before each request
   * @param path request path
   * @param options request options
   * @returns request options
   * @example
   * const { url, requestOptions, errorOptions } = this.onBeforeRequest('/user', { params: { id: 1 } })
   * console.log(url, requestOptions, errorOptions)
   * @overload onBeforeRequest(path: string): RequestOptions
   * @overload onBeforeRequest(path: string, options: Options): RequestOptions
   * @see RequestOptions
   * @see Options
   * @see SerializationStrategy
   * @see getURLSearchString
   */
  onBeforeRequest(path: string, options?: Options) {
    const { params = {}, serializationStrategy = getURLSearchString } = options || {}
    const search = Object.keys(params).length !== 0 ? `?${serializationStrategy(params)}` : ''

    let requestOptions: RequestOptions = {}
    // add token to request headers
    // update according to your authentication method
    const token = getCurrentToken()
    if (token) {
      requestOptions = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    }
    // end of token addition

    const url = this.baseUrl + path + search
    const errorOptions = {
      disableDefaultErrorDisplay: options?.disableDefaultErrorDisplay || false,
      notifyOnError: options?.notifyOnError || false,
      errorLoggingHandledUpstream: options?.errorLoggingHandledUpstream || false,
    }

    return { url, requestOptions, errorOptions }
  }

  /**
   * This method is called when an error occurs
   * @param error error object
   * @param errorOptions error options
   * @throws error
   * @example
   * this.onError(new Error('Unauthorized'), { disableDefaultErrorDisplay: true })
   * @see ErrorOptions
   */
  onError(error: unknown, errorOptions: ErrorOptions) {
    // handle common errors here
    if (error instanceof HttpError) {
      if (!errorOptions.errorLoggingHandledUpstream) {
        logError('HttpError', error)
      }
      if (error.status === 401) {
        removeTokenAtom()
        window.location.href = buildUrl(
          `/login?redirect=${encodeURIComponent(`${location.pathname}${location.search}`)}`
        )
      }

      if (error.status === 403) {
        // redirect to forbidden page
      }

      if (error.status === 404) {
        // redirect to not found page
      }

      if (error.status === 500) {
        // redirect to internal server error page
      }
    }
    if (errorOptions.disableDefaultErrorDisplay) {
      // update ui here
    }

    // call custom error handling
    if (this.onErrorCB) {
      this.onErrorCB(error, errorOptions)
    }

    // add ship only error handling here
    throw error
  }

  /**
   * Make a GET request
   * @param path request path
   * @param options request options
   * @returns response data
   * @throws error
   * @example
   * const response = await client.get<UserResponse>('/user', { params: { id: 1 } })
   * console.log(response)
   * @overload get<TResponse>(path: string): Promise<TResponse>
   * @overload get<TResponse>(path: string, options: Options): Promise<TResponse>
   * @see Options
   */
  async get<TResponse>(path: string, options?: Options): Promise<TResponse> {
    const { url, requestOptions, errorOptions } = this.onBeforeRequest(path, options)
    try {
      const response = await HttpBaseApi.get<TResponse>(url, requestOptions)
      return response.data
    } catch (error) {
      this.onError(error, errorOptions)
      throw error
    }
  }

  async getFile(path: string, options?: Options): Promise<Blob> {
    const { url, requestOptions, errorOptions } = this.onBeforeRequest(path, options)
    try {
      /**
       * Note: in HttpBaseApi.get, there's a try..catch block that tries to return data: await response.json(),
       * but falls back to data: response if json() errors out. In the case of file download, this fallback is the
       * path that's used, which is why this is typed as Response
       *
       * But it doesn't work since once .json() is called, the response body is consumed and can't be used again, even if it errors out.
       *
       * so for now using fetch directly
       */
      const response = await fetch(url, requestOptions)
      return await response.blob()
    } catch (error) {
      this.onError(error, errorOptions)
      throw error
    }
  }

  /**
   * Make a POST request
   * @param path request path
   * @param body request body
   * @param options request options
   * @returns response data
   * @throws error
   * @example
   * const response = await client.post<UserResponse>('/user', { email: '', password: '' })
   * console.log(response)
   * @overload post<TResponse>(path: string): Promise<TResponse>
   * @overload post<TResponse>(path: string, body: object): Promise<TResponse>
   * @overload post<TResponse>(path: string, options: Options): Promise<TResponse>
   * @overload post<TResponse>(path: string, body: object, options: Options): Promise<TResponse>
   * @see Options
   */
  async post<TResponse>(path: string): Promise<TResponse>
  async post<TResponse>(path: string, body: object): Promise<TResponse>
  async post<TResponse>(path: string, options?: Options): Promise<TResponse>
  async post<TResponse>(path: string, body?: object, options?: Options): Promise<TResponse>
  async post<TResponse>(path: string, body?: object, options?: Options): Promise<TResponse> {
    const { url, requestOptions, errorOptions } = this.onBeforeRequest(path, options)
    try {
      const response = await HttpBaseApi.post<TResponse>(url, body, requestOptions)
      return response.data
    } catch (error) {
      this.onError(error, errorOptions)
      throw error
    }
  }

  /**
   * Make a PUT request
   * @param path request path
   * @param body request body
   * @param options request options
   * @returns response data
   * @throws error
   * @example
   * const response = await client.put<UserResponse>('/user', { email: '', password: '' })
   * console.log(response)
   * @overload put<TResponse>(path: string): Promise<TResponse>
   * @overload put<TResponse>(path: string, body: object): Promise<TResponse>
   * @overload put<TResponse>(path: string, options: Options): Promise<TResponse>
   * @overload put<TResponse>(path: string, body: object, options: Options): Promise<TResponse>
   * @see Options
   */
  async put<TResponse>(path: string): Promise<TResponse>
  async put<TResponse>(path: string, body: object): Promise<TResponse>
  async put<TResponse>(path: string, options: Options): Promise<TResponse>
  async put<TResponse>(path: string, body?: object, options?: Options): Promise<TResponse>
  async put<TResponse>(path: string, body?: object, options?: Options): Promise<TResponse> {
    const { url, requestOptions, errorOptions } = this.onBeforeRequest(path, options)
    try {
      const response = await HttpBaseApi.put<TResponse>(url, body, requestOptions)
      return response.data
    } catch (error) {
      this.onError(error, errorOptions)
      throw error
    }
  }

  /**
   * Make a PATCH request
   * @param path request path
   * @param body request body
   * @param options request options
   * @returns response data
   * @throws error
   * @example
   * const response = await client.patch<UserResponse>('/user', { email: '', password: '' })
   * console.log(response)
   * @overload patch<TResponse>(path: string): Promise<TResponse>
   * @overload patch<TResponse>(path: string, body: object): Promise<TResponse>
   * @overload patch<TResponse>(path: string, options: Options): Promise<TResponse>
   * @overload patch<TResponse>(path: string, body: object, options: Options): Promise<TResponse>
   * @see Options
   */
  async patch<TResponse>(path: string): Promise<TResponse>
  async patch<TResponse>(path: string, body: object): Promise<TResponse>
  async patch<TResponse>(path: string, options: Options): Promise<TResponse>
  async patch<TResponse>(path: string, body?: object, options?: Options): Promise<TResponse>
  async patch<TResponse>(path: string, body?: object, options?: Options): Promise<TResponse> {
    const { url, requestOptions, errorOptions } = this.onBeforeRequest(path, options)
    try {
      const response = await HttpBaseApi.patch<TResponse>(url, body, requestOptions)
      return response.data
    } catch (error) {
      this.onError(error, errorOptions)
      throw error
    }
  }

  /**
   * Make a DELETE request
   * @param path request path
   * @param options request options
   * @returns response data
   * @throws error
   * @example
   * const response = await client.delete<UserResponse>('/user', { params: { id: 1 } })
   * console.log(response)
   * @overload delete<TResponse>(path: string): Promise<TResponse>
   * @overload delete<TResponse>(path: string, options: Options): Promise<TResponse>
   * @see Options
   */
  async delete<TResponse>(path: string): Promise<TResponse>
  async delete<TResponse>(path: string, body: object): Promise<TResponse>
  async delete<TResponse>(path: string, options: Options): Promise<TResponse>
  async delete<TResponse>(path: string, body?: object, options?: Options): Promise<TResponse>
  async delete<TResponse>(path: string, body?: object, options?: Options): Promise<TResponse> {
    const { url, requestOptions, errorOptions } = this.onBeforeRequest(path, options)
    try {
      const response = await HttpBaseApi.delete<TResponse>(url, body, requestOptions)
      return response.data
    } catch (error) {
      this.onError(error, errorOptions)
      throw error
    }
  }

  /** Upload a binary file */
  private async uploadFile<TResponse>(
    method: string,
    path: string,
    file: File,
    body?: object,
    options?: Options
  ): Promise<TResponse> {
    const { url, requestOptions, errorOptions } = this.onBeforeRequest(path, options)
    try {
      const formData = new FormData()
      formData.append('file', file)
      Object.entries(body ?? {}).forEach(([key, value]) => {
        formData.append(key, value)
      })

      const response = await fetch(url, {
        headers: requestOptions?.headers,
        method: method,
        signal: requestOptions?.signal || null,
        body: formData,
      })

      return await response.json()
    } catch (error) {
      this.onError(error, errorOptions)
      throw error
    }
  }

  /** Upload a binary file via POST */
  async postFile<TResponse>(path: string, file: File, body?: object, options?: Options): Promise<TResponse> {
    return this.uploadFile<TResponse>('post', path, file, body, options)
  }

  /** Upload a binary file via PUT */
  async putFile<TResponse>(path: string, file: File, body?: object, options?: Options): Promise<TResponse> {
    return this.uploadFile<TResponse>('put', path, file, body, options)
  }
}

export default Client
